import {Map as OlMap, Overlay} from 'ol';
import Feature from 'ol/Feature';
import {Circle, LineString, Point, Polygon} from 'ol/geom';
import {Cluster, Vector as VectorSource} from 'ol/source';
import {createEmpty as CreateEmpty, extend} from 'ol/extent';
import {Tile as TileLayer, Vector as VectorLayer, VectorImage as VectorImageLayer} from 'ol/layer';
import {fromLonLat, getPointResolution, METERS_PER_UNIT, transform} from 'ol/proj';
import XYZ from 'ol/source/XYZ';
import {Circle as CircleStyle, Fill, Icon, Stroke, Style, Text as TextStyle} from 'ol/style';
import {getCursor, getDeviceIcon, getDeviceIconColor, getSelectionPin} from '../../helpers/deviceIcons';
import {fadeColor} from '../../helpers/report';
import {isCoincident, spiderfyCoordinates} from './spiderfy';

const SPIDERFY_RADIUS_PX = 20;
const SPIDERFY_MIN_ZOOM = 15;

// Margem e zoom máximo do ajuste ao foco da cerca, os mesmos do ajuste ao relatório: uma cerca
// pequena não aproxima o mapa além do nível de rua.
const GEOFENCE_FOCUS_PADDING = [40, 40, 40, 40];
const GEOFENCE_FOCUS_MAX_ZOOM = 17;
const GEOFENCE_FOCUS_COLOR = '#ff6a00';

/**
 * Map Class Instance
 */
class MapInstance {
  constructor(options) {
    this._instance = new OlMap(options);

    this._devicesLayer = null;
    this._devicesRawSource = null;
    this._devices = new Map();
    this._pendingReappearanceIds = new Set();
    this._trackedDisabledIds = new Set();

    this._traceEventLayer = null;

    this._traceLayer = null;
    this._traces = new Map();

    this._geofenceLayer = null;
    this._geofenceFocusLayer = null;

    this._reportLayer = null;
    this._replayLayer = null;
    this.selectedReportPoint = null;
    this.selectedReportDevice = null;
    this._reportSelectionPin = null;
    this._reportBlocos = new Map();

    this._cacheImg = {};

    this._i18n = null;

    this.isFollowAnimating = false;
  }

  get instance() {
    return this._instance;
  }

  get devicesLayer() {
    return this._devicesLayer;
  }

  get devices() {
    return this._devices;
  }

  get zoom() {
    return this._instance.getView().getZoom();
  }

  get i18n() {
    return this._i18n;
  }

  setI18n(translateService) {
    this._i18n = translateService;
  }

  static createTileXYZ(url, name, visible = false) {
    return new TileLayer({
      source: new XYZ({url}),
      visible,
      name,
    });
  }

  static createCoordinate([longitude, latitude]) {
    return fromLonLat([longitude, latitude]);
  }

  increaseZoom() {
    this._instance.getView().setZoom(this._instance.getView().getZoom() + 1);
  }

  decreaseZoom(value) {
    if (value) {
      this._instance.getView().setZoom(this._instance.getView().getZoom() - value);
    } else {
      this._instance.getView().setZoom(this._instance.getView().getZoom() - 1);
    }
  }

  goTo(coord, zoom) {
    const options = {};
    if (coord) options.center = fromLonLat(coord);
    if (zoom) options.zoom = zoom;
    this._instance.getView().animate(options);
  }

  fitReportExtent(padding = [40, 40, 40, 40], maxZoom = 17) {
    const extent = this._reportLayer.getSource().getExtent();
    if (!extent.every(Number.isFinite)) return;

    this._instance.getView().fit(extent, {
      size: this._instance.getSize(),
      padding,
      maxZoom,
      duration: 500,
    });
  }


  _replayMarkerStyle(iconSrc) {
    return (f) => new Style({
      image: new Icon({
        src: iconSrc,
        anchor: [0.5, 0.5],
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        rotation: (f.get('data').course * Math.PI) / 180,
      }),
    });
  }

  // Escolher pela lista so mudava a propriedade, e o OpenLayers nao repinta por causa disso: o
  // realce aparecia apenas quando algo mais forcava o desenho, como clicar no proprio marcador.
  // Aqui a camada e avisada, entao lista e mapa acendem o mesmo ponto.
  setSelectedReportPoint(pointId, deviceId = null, lonLat = null) {
    this.selectedReportPoint = pointId ?? null;
    this.selectedReportDevice = deviceId != null ? Number(deviceId) : null;

    this.removeReportSelectionPin();

    // O relatorio grande desenha um marcador a cada N posicoes, entao a posicao escolhida quase
    // nunca tem feicao propria -- realcar uma feicao que nao existe nao mostra nada. O pino e
    // desenhado por conta propria, na coordenada escolhida, com o mesmo deslocamento do traco
    // daquele veiculo para cair sobre a linha e nao ao lado dela. Quando a posicao tem marcador
    // proprio, o estilo ativo dele ja desenha o pino, e um segundo ficaria sobreposto.
    if (lonLat && this._reportLayer && !this._reportLayer.getSource().getFeatureById(this.selectedReportPoint)) {
      const bloco = this._reportBlocos?.get(this.selectedReportDevice);
      const [x, y] = fromLonLat(lonLat);
      const deslocamento = bloco?.deslocamento || 0;

      const pin = new Feature({geometry: new Point([x + deslocamento, y + deslocamento])});
      pin.setStyle(
        new Style({
          image: new Icon({
            src: getSelectionPin(bloco?.color || 'rgb(22, 165, 218)'),
            anchor: [0.5, 1],
            scale: 0.55,
          }),
          zIndex: 8,
        }),
      );

      this._reportSelectionPin = pin;
      this._reportLayer.getSource().addFeature(pin);
    }

    if (this._reportLayer) this._reportLayer.changed();
  }

  removeReportSelectionPin() {
    if (!this._reportSelectionPin || !this._reportLayer) return;
    this._reportLayer.getSource().removeFeature(this._reportSelectionPin);
    this._reportSelectionPin = null;
  }

  addReplayMarker(coord, course, iconSrc = getCursor('rgb(22, 165, 218)')) {
    this.removeReplayMarker();

    const feature = new Feature({geometry: new Point(fromLonLat(coord))});
    feature.set('data', {course});
    feature.setStyle(this._replayMarkerStyle(iconSrc));

    this._replayMarker = feature;
    this._replayLayer.getSource().addFeature(feature);
  }

  updateReplayMarker(coord, course, iconSrc) {
    if (!this._replayMarker) return;
    this._replayMarker.get('data').course = course;
    this._replayMarker.getGeometry().setCoordinates(fromLonLat(coord));
    if (iconSrc) this._replayMarker.setStyle(this._replayMarkerStyle(iconSrc));
  }

  removeReplayMarker() {
    if (!this._replayMarker) return;
    this._replayLayer.getSource().removeFeature(this._replayMarker);
    this._replayMarker = null;
  }

  setMap(name) {
    this._instance
      .getLayers()
      .forEach(
        (t) => t instanceof TileLayer && t.setVisible(t.get('name') === name),
      );
  }

  initDevicesLayer() {
    this._devicesRawSource = new VectorSource();

    const layer = new VectorLayer({
      source: this._devicesRawSource,
      name: 'devicesLayer',
      zIndex: 10,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });

    this._devicesLayer = layer;

    this.instance.addLayer(layer);
  }

  initReportLayer() {
    // VectorImage caches the layer as a raster and cheaply
    // transforms it during pan/zoom instead of replaying every feature's
    // draw commands each frame - needed here since routes can carry
    // thousands of point markers.
    const layer = new VectorImageLayer({
      source: new VectorSource(),
      name: 'reportLayer',
      zIndex: 11,
    });

    this._reportLayer = layer;

    this.instance.addLayer(layer);
  }

  initReplayLayer() {
    const layer = new VectorLayer({
      source: new VectorSource(),
      name: 'replayLayer',
      zIndex: 12,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });

    this._replayLayer = layer;

    this.instance.addLayer(layer);
  }

  initEventLayer() {
    const layer = new VectorLayer({
      source: new VectorSource(),
      name: 'eventLayer',
    });

    this.instance.addLayer(layer);
    this._eventLayer = layer;
  }

  initTraceLayer() {
    const cacheCursor = {};

    const styleFunction = (feature) => {
      const geometry = feature.getGeometry();
      const {color, courses} = feature.get('data');

      if (!cacheCursor[color]) cacheCursor[color] = getCursor(color);

      // linestring
      const styles = [
        new Style({
          stroke: new Stroke({color: 'white', width: 8}),
        }),
        new Style({
          stroke: new Stroke({color, width: 6}),
        }),
      ];

      let lastDistance = 0;
      let idx = 0;
      geometry.forEachSegment((start, end) => {
        const distance = this._getDistance(start, end);

        if (idx > 0 && distance + lastDistance > 20) {
          lastDistance = 0;

          styles.push(
            new Style({
              geometry: new Point(start),
              image: new Icon({
                src: cacheCursor[color],
                rotation: (courses[idx] * Math.PI) / 180,
              }),
            }),
          );
        } else {
          lastDistance += distance;
        }
        idx++;
      });

      if (geometry.getCoordinates().length > 1) {
        styles.push(
          new Style({
            geometry: new Point(geometry.getLastCoordinate()),
            image: new CircleStyle({
              radius: 7,
              fill: new Fill({color}),
              stroke: new Stroke({color: 'white', width: 2}),
            }),
          }),
        );
      }

      return styles;
    };

    const layer = new VectorLayer({
      source: new VectorSource(),
      style: styleFunction,
      name: 'traceLayer',
    });

    if (this._traceLayer) {
      this._forgetTraces(this._traceLayer.getSource().getFeatures());
      this.instance.removeLayer(this._traceLayer);
    }

    this._traceLayer = layer;

    this.instance.addLayer(layer);
  }

  initTraceEventLayer() {
    const styleFunction = (feature) => {
      const geometry = feature.getGeometry();
      const {color, courses} = feature.get('data');

      const cursor = getCursor('red');

      // linestring
      const styles = [
        new Style({
          stroke: new Stroke({color: 'white', width: 8}),
        }),
        new Style({
          stroke: new Stroke({color, width: 6}),
        }),
      ];

      if (geometry.getCoordinates().length > 1) {
        styles.push(
          new Style({
            geometry: new Point(geometry.getLastCoordinate()),
            image: new Icon({
              scale: 1.5,
              src: cursor,
              rotation: (courses[courses.length - 1] * Math.PI) / 180,
            }),
          }),
        );
      }

      return styles;
    };

    const layer = new VectorLayer({
      source: new VectorSource(),
      style: styleFunction,
      name: 'traceLayer',
    });

    this._traceEventLayer = layer;

    this.instance.addLayer(layer);
  }

  initGeofencesLayer() {
    const layer = new VectorLayer({
      source: new VectorSource(),
      name: 'geofenceLayer',
    });

    this._geofenceLayer = layer;

    this.instance.addLayer(layer);
  }

  defineDeviceCluster(distance, zoom) {
    // Sempre a partir da fonte bruta (nunca this._devicesLayer.getSource(), que depois da
    // primeira chamada ja e o Cluster da vez anterior) -- chamar de novo (agora acontece a
    // cada troca de conjunto de dispositivos, MON-570) empilhava Cluster dentro de Cluster.
    // Na segunda chamada em diante, a feature interna virava um cluster sintetico sem
    // .getStyle() (so features reais tem, setado em addFeatureDevice), quebrando o style
    // function da linha abaixo (TypeError: e.get(...)[0].getStyle(...) is not a function).
    const clusterSource = new Cluster({
      source: this._devicesRawSource,
      distance,
    });

    this._devicesLayer.setSource(clusterSource);

    const styleCache = {};

    this._devicesLayer.setStyle((feature) => {
      const size = feature.get('features').length;
      if (size === 1) {
        const f = feature.get('features')[0];
        return feature.get('features')[0].getStyle()(f);
      } else {
        let style = styleCache[size];

        const radius = Math.max(15, Math.min(size * 2, 20));
        let dash = (2 * Math.PI * radius) / 6;
        dash = [0, dash, dash, dash, dash, dash, dash];
        const color = size > 25 ? '49,27,146' : size > 8 ? '81,45,168' : '126,87,194';

        if (!style) {
          style = new Style({
            image: new CircleStyle({
              radius: radius,
              stroke: new Stroke({
                color: 'rgba(' + color + ',0.5)',
                width: 15,
                lineDash: dash,
                lineCap: 'butt',
              }),
              fill: new Fill({color: 'rgba(' + color + ',1)'}),
            }),
            text: new TextStyle({
              font: '15px sans-serif',
              text: size.toString(),
              fill: new Fill({color: '#fff'}),
            }),
          });
          styleCache[size] = style;
        }
        return style;
      }
    });

    if (zoom) {
      this._devicesLayer.once('change', (evt) => {
        console.log(evt);
        setTimeout(() => {
          this.centerOnCluster();
        }, 1000);
      });
    }
  }

  addFeatureEvent() {
  }

  centerOnCluster() {
    const source = this._devicesLayer.getSource();
    const features = source.getFeatures();
    const extent = new CreateEmpty();

    features.forEach((f, index, array) => {
      extend(extent, f.getGeometry().getExtent());
    });

    const validIndexValue = extent.findIndex((el) => el == Number.POSITIVE_INFINITY || el == Number.NEGATIVE_INFINITY);
    if (validIndexValue < 0) {
      this._instance.getView().fit(extent, {
        size: this._instance.getSize(),
        duration: 1000,
        maxZoom: 12,
        padding: [25, 25, 25, 25],
      });
    }
  }


  spiderfyCluster(clusterFeature) {
    const features = clusterFeature.get('features');
    if (!features || features.length <= 1) return;

    const coords = features.map((f) => f.getGeometry().getCoordinates());
    if (!isCoincident(coords)) return; // cluster normal: zoom resolve, não mexe
    if (this._instance.getView().getZoom() < SPIDERFY_MIN_ZOOM) return; // longe demais: raio real ficaria enorme

    const resolution = this._instance.getView().getResolution();
    const spread = spiderfyCoordinates(coords, SPIDERFY_RADIUS_PX * resolution);
    features.forEach((f, i) => f.getGeometry().setCoordinates(spread[i]));
  }

  addFeatureDevice(device) {
    if (!device.currentPosition) {
      // Dispositivo recem-concedido (MON-570): o replot da frota roda antes da posicao dele
      // chegar pelo socket, entao cai aqui sem nunca ganhar marcador. Nada mais tenta criar
      // a feature depois -- syncDevicePosition (useMapPosition.js) so recria quando
      // consumePendingReappearance for true. Marcar aqui garante que, quando a posicao
      // chegar, o marcador nasce (mesmo mecanismo ja usado pro disabled/enabled).
      this.markPendingReappearance(device.id);
      return;
    }
    if (device.disabled) {
      this._trackedDisabledIds.add(device.id);
      return;
    }
    const {category, status, id} = device;

    const isDeviceFeatureAlreadyExists = !!this.getDeviceFeatureByDeviceId(id);
    if (isDeviceFeatureAlreadyExists) return;

    const {
      longitude,
      latitude,
      course,
      valid,
      attributes = {},
    } = device.currentPosition;

    const feature = new Feature({
      geometry: new Point(fromLonLat([longitude, latitude])),
    });

    const img = new Image(50, 50);

    const ignition = !!attributes.ignition;
    const alarm = !!attributes.alarm;

    img.onload = () => feature.changed();

    img.src = getDeviceIcon(
      category,
      'top',
      getDeviceIconColor({status, ignition, alarm}),
    );

    this._cacheImg[`${category}_${status}_${ignition.toString()}`] = img;

    img.onerror = (err) => {
      img.onload = () => feature.changed();
      img.src = getDeviceIcon(
        'default',
        'top',
        getDeviceIconColor({status, ignition}),
      );
      this._cacheImg[`${category}_${status}_${ignition.toString()}`] = img;
    };

    feature.set('data', {img, course, valid});
    feature.setId(+id);
    feature.setStyle((feature) => {
      const {img, course, valid} = feature.get('data');

      return new Style({
        image: new Icon({
          img: img,
          anchor: [0.5, 0.5],
          crossOrigin: 'anonymous',
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction',
          imgSize: [img.width, img.height],
          rotation: (course * Math.PI) / 180,
          // sem fix GPS válido -> ícone semi-transparente, não esconde o
          // device nem reaproveita o status "unknown" (que já significa
          // outra coisa: device sem reportar, não posição sem fix)
          opacity: valid === false ? 0.4 : 1,
        }),
      });
    });

    this._devices.set(id, feature);
    this._devicesRawSource.addFeature(feature);
  }

  addFeatureTrace(deviceId, coords, color) {
    const courses = [];

    coords = coords.map(([lon, lat, course]) => {
      courses.push(course);
      return fromLonLat([lon, lat]);
    });

    const feature = new Feature({
      geometry: new LineString(coords),
      name: 'trace',
    });

    feature.set('data', {courses, color});
    feature.setId(+deviceId);

    this._traceLayer.getSource().addFeature(feature);
    this._traces.set(deviceId, feature);
  }

  addFeatureEventTrace(deviceId, coords, color) {
    const courses = [];

    coords = coords.map(([lon, lat, course]) => {
      courses.push(course);
      return fromLonLat([lon, lat]);
    });

    const feature = new Feature({
      geometry: new LineString(coords),
      name: 'trace',
    });

    feature.set('data', {courses, color});
    feature.setId(+deviceId);

    this._traceEventLayer.getSource().addFeature(feature);
    this._traces.set(deviceId, feature);
  }

  addLabelOverlay(deviceId, name, coord) {
    // useMapLabel.js reroda o efeito que chama isto pra toda a frota sempre que deviceMap
    // muda (agora acontece a cada troca de conjunto de dispositivos, MON-570) -- sem esse
    // guard, um device que continua no mesmo usuario entre duas trocas ganhava um overlay
    // de label novo a cada vez, sem nunca remover o antigo (Overlay do OL nao dedup por id
    // sozinho). Mesmo padrao ja usado em addFeatureDevice pro icone do veiculo.
    if (this.findOverlayById(`label-${deviceId}`)) return;

    const element = document.createElement('div');
    element.className = 'label-overlay rounded-pill';
    element.innerHTML = name;

    const overlay = new Overlay({
      id: `label-${deviceId}`,
      element,
      offset: [0, -30],
      positioning: 'bottom-center',
      position: fromLonLat(coord),
    });

    overlay.set('name', 'label');
    this._instance.addOverlay(overlay);
  }

  addAlarmOverlay(deviceId, alarmName, coord) {
    const element = document.createElement('div');
    element.className = 'label-overlay-alarm rounded-pill';
    const alarm = this._i18n.formatMessage({
      id: `alarm${String(alarmName)
        .charAt(0)
        .toUpperCase()}${String(alarmName).slice(1)}`,
    });
    element.innerHTML = `<span name="alarm-icon" class="far fa-siren-on"></span> <span name="alarm-name" class="alarm-name">${alarm}</span>`;

    const overlay = new Overlay({
      id: `alarm-${deviceId}`,
      element,
      offset: [0, 27],
      positioning: 'top-left',
      position: fromLonLat(coord),
    });

    overlay.set('name', 'alarm');
    this._instance.addOverlay(overlay);
  }

  addEventOverlay(elementId, coord) {
    const container = document.getElementById(elementId);

    const overlay = new Overlay({
      id: 'overlayEvent',
      positioning: 'bottom-left',
      element: container,
      autoPan: true,
      position: fromLonLat(coord),
      stopEvent: false,
      autoPanAnimation: {
        duration: 250,
      },
    });

    overlay.set('name', 'event');
    this._instance.addOverlay(overlay);
  }

  // `markerStep` diz de quantas em quantas posicoes desenhar um marcador: zero nao desenha nenhum
  // (so inicio e fim), um desenha todos, e N desenha um a cada N. Cada marcador custa uma feicao e
  // um estilo, entao em relatorio grande e este numero que segura o custo do desenho.
  addReportRoute(data, markerStep = 0) {
    this.removeReports();

    // Veiculos da mesma linha percorrem as mesmas coordenadas, entao os tracos ficam exatamente um
    // sobre o outro e so o ultimo desenhado aparece. Com mais de um veiculo, cada traco entra
    // deslocado alguns metros para o lado, alternando os lados, o suficiente para os quatro serem
    // vistos. Relatorio de um veiculo so nao desloca nada: e o que se pede quando o traco precisa
    // valer como prova de onde o veiculo esteve.
    const DESLOCAMENTO_METROS = 8;
    const varios = data.length > 1;
    const deslocamentoDe = (indice) => {
      if (!varios) return 0;
      const passo = Math.ceil((indice + 1) / 2);
      return (indice % 2 === 0 ? passo : -passo) * DESLOCAMENTO_METROS;
    };

    // Cor e deslocamento de cada dispositivo ficam guardados: o pino do ponto escolhido precisa sair
    // na cor certa e sobre o traco desenhado, e nao sobre a coordenada crua.
    this._reportBlocos = new Map();

    const features = data.reduce((features, bloco, indiceDoBloco) => {
      // Cada bloco e um dispositivo, com a cor que a lista tambem usa no nome dele. O marcador comum
      // sai numa versao clara dessa cor e o selecionado na cor cheia -- assim o ponto escolhido
      // salta no meio dos outros sem precisar trocar de cor e perder de quem ele e.
      const {color, positions} = bloco;
      const fadedColor = fadeColor(color, 0.45);
      const cursor = getCursor(fadedColor);
      const activeCursor = getCursor(color);
      const realceSrc = getSelectionPin(color);
      const courses = [];
      const ids = [];

      const deslocamento = deslocamentoDe(indiceDoBloco);
      if (bloco.deviceId != null) {
        this._reportBlocos.set(Number(bloco.deviceId), {color: bloco.color, deslocamento});
      }
      const coords = positions.map(({id, lon, lat, course}) => {
        courses.push(course);
        ids.push(id);
        const [x, y] = fromLonLat([lon, lat]);
        return [x + deslocamento, y + deslocamento];
      });

      const lineFeature = new Feature({
        geometry: new LineString(coords),
        name: 'line',
      });

      // Com uma linha selecionada, a dela vai para a frente em cor cheia e as outras recuam
      // apagadas -- e assim que se enxerga um veiculo no meio de varios na mesma rota.
      const deviceId = bloco.deviceId;
      // Traco sempre por baixo: marcador coberto pela propria linha nao serve para nada. A ordem e
      // linha, marcador, marcador escolhido, pino.
      const lineAtiva = new Style({stroke: new Stroke({color, width: 7}), zIndex: 1});
      const lineNormal = new Style({stroke: new Stroke({color, width: 6}), zIndex: 0});
      const lineApagada = new Style({stroke: new Stroke({color: fadedColor, width: 4}), zIndex: 0});

      lineFeature.setStyle(() => {
        if (this.selectedReportDevice == null) return lineNormal;
        return this.selectedReportDevice === deviceId ? lineAtiva : lineApagada;
      });

      const length = coords.length;
      const positionsFeature = coords.reduce((features, coord, index) => {
        const meio = index > 0 && index < length - 1;
        if (meio && (!markerStep || index % markerStep !== 0)) return features;

        const f = new Feature({
          geometry: new Point(coord),
        });

        f.setId(ids[index]);

        // `forte` engrossa o traco e sobe o ponto acima dos demais: e o que faz o marcador
        // selecionado se destacar, sem mudar a cor do dispositivo.
        const buildStyle = (strokeColor, iconSrc, forte = false) => {
          const width = forte ? 5 : 3;
          const zIndex = forte ? 4 : 2;

          if (index === 0) {
            return new Style({
              image: new CircleStyle({
                radius: forte ? 10 : 8,
                fill: new Fill({color: 'white'}),
                stroke: new Stroke({color: strokeColor, width}),
              }),
              zIndex,
            });
          }
          if (index === length - 1) {
            return [
              new Style({
                image: new CircleStyle({
                  radius: forte ? 12 : 10,
                  fill: new Fill({color: 'white'}),
                  stroke: new Stroke({color: strokeColor, width}),
                }),
                zIndex,
              }),
              new Style({
                image: new CircleStyle({radius: 3, fill: new Fill({color: strokeColor})}),
                zIndex,
              }),
            ];
          }
          const seta = new Style({
            image: new Icon({
              src: iconSrc,
              rotation: (courses[index] * Math.PI) / 180,
              // O pino ja marca o ponto; a seta so acompanha, em cor cheia e um pouco maior.
              scale: forte ? 1.2 : 1,
            }),
            zIndex,
          });

          if (!forte) return seta;

          // O ponto escolhido ganha um pino por cima da seta. So aumentar e escurecer a seta nao
          // bastava: no meio de centenas de setas iguais, quem nao sabe o que procurar nao acha.
          return [
            seta,
            new Style({
              // Ponta do pino no ponto exato, e reduzido: em tamanho cheio ele cobria o trecho da
              // rota que a pessoa quer justamente olhar.
              image: new Icon({src: realceSrc, anchor: [0.5, 1], scale: 0.55}),
              zIndex: zIndex + 2,
            }),
          ];
        };

        // Styles built once per feature (not per render) - OL invokes
        // the style function on every render/zoom/pan, and building fresh
        // Style/Icon graphs there was the O(n) cost behind the zoom-out lag.
        const inactiveStyle = buildStyle(fadedColor, cursor);
        const activeStyle = buildStyle(color, activeCursor, true);

        f.setStyle((feature) => (feature.getId() === this.selectedReportPoint ? activeStyle : inactiveStyle));
        return [...features, f];
      }, []);

      return [...features, lineFeature, ...positionsFeature];
    }, []);

    this._reportLayer.getSource().addFeatures(features);
  }

  addReportTrip(data, markerStep = 0, id) {
    this.removeReports();

    const color = 'rgb(22, 165, 218)';
    const cursor = getCursor(color);

    const courses = [];

    const coords = data.map(({longitude, latitude, course}) => {
      courses.push(course);
      return fromLonLat([longitude, latitude]);
    });

    const lineFeature = new Feature({
      geometry: new LineString(coords),
      name: 'line',
    });

    lineFeature.setStyle(
      new Style({stroke: new Stroke({color, width: 6})}),
    );

    const length = coords.length;
    const features = coords.reduce((features, coord, index) => {
      const meio = index > 0 && index < length - 1;
      if (meio && (!markerStep || index % markerStep !== 0)) return features;

      const f = new Feature({geometry: new Point(coord)});
      f.set('id', id);
      f.setStyle(
        index === 0 ?
          new Style({
            image: new CircleStyle({
              radius: 8,
              fill: new Fill({color: 'white'}),
              stroke: new Stroke({
                color,
                width: 3,
              }),
            }),
            zIndex: 1,
          }) :
          index === length - 1 ?
            [
              new Style({
                image: new CircleStyle({
                  radius: 10,
                  fill: new Fill({color: 'white'}),
                  stroke: new Stroke({
                    color,
                    width: 3,
                  }),
                }),
                zIndex: 1,
              }),
              new Style({
                image: new CircleStyle({
                  radius: 3,
                  fill: new Fill({color}),
                }),
                zIndex: 1,
              }),
            ] :
            new Style({
              image: new Icon({
                src: cursor,
                rotation: (courses[index] * Math.PI) / 180,
              }),
            }),
      );

      return [...features, f];
    }, []);

    this._reportLayer.getSource().addFeatures([lineFeature, ...features]);
  }

  addReportPoint(data) {
    if (!data) {
      this.removeReports();
      return;
    }

    if (this._reportLayer.getSource().getFeatureById(String(data.id))) return;

    const color = 'rgb(22, 165, 218)';
    const cursor = getCursor(color);
    const activeCursor = getCursor('#FC0000');

    const {id, course, longitude, latitude} = data;

    const feature = new Feature({
      geometry: new Point(fromLonLat([longitude, latitude])),
    });
    feature.setId(id);
    feature.setStyle((f) => {
      const active = f.getId() === this.selectedReportPoint;
      return new Style({
        image: new Icon({
          src: active ? activeCursor : cursor,
          rotation: (course * Math.PI) / 180,
        }),
      });
    });

    this._reportLayer.getSource().addFeature(feature);
  }

  addGeofence(geofence, zoom) {
    const {area, id} = geofence;
    const feature = new Feature(this.wktToGeometry(area));

    feature.setId(+id);
    feature.setStyle([
      new Style({
        stroke: new Stroke({
          color: 'blue',
          width: 3,
        }),
        fill: new Fill({
          color: 'rgba(0, 0, 255, 0.1)',
        }),
      }),
    ]);

    if (this._geofenceLayer === null) {
      this.initGeofencesLayer();
    }

    this._geofenceLayer.getSource().addFeature(feature);

    if (zoom) {
      const point = feature.getGeometry();
      this._instance.getView().fit(point);
    }
  }

  removeGeofence(geofenceId) {
    if (!this._geofenceLayer) return;

    const source = this._geofenceLayer.getSource();
    if (geofenceId) {
      const found = source.getFeatures().find((f) => f.getId() === geofenceId);
      found && source.removeFeature(found);
      return;
    }
    source.getFeatures().forEach((f) => source.removeFeature(f));
  }

  /**
   * Realça uma cerca e mostra, junto, a posição de um evento, ajustando o mapa aos dois.
   *
   * BUSINESS RULE: a cerca é desenhada numa camada própria, por cima das demais, com um estilo que a
   * distingue das outras cercas. Como não depende da camada das cercas, aparece mesmo com elas
   * escondidas, e esconder ou mostrar as cercas não a afeta nem é afetado por ela.
   * BUSINESS RULE: cada chamada que se aplica troca o foco anterior; `clearGeofenceFocus` o tira.
   * CONSTRAINT: a animação em curso é interrompida antes do ajuste, senão ela continua em paralelo e
   * puxa o mapa de volta ao destino dela.
   *
   * @param {Object} geofence cerca (`id`, `area`)
   * @param {number[]|null} eventLonLat `[longitude, latitude]` da posição do evento, se houver
   * @return {boolean} `false` quando a área da cerca não se lê (tipo desconhecido, círculo sem raio
   *   numérico): nada é desenhado nem movido, e o foco anterior fica como está
   */
  focusGeofence(geofence, eventLonLat = null) {
    const geometry = geofence && geofence.area ? this.wktToGeometry(geofence.area) : null;
    if (!geometry) return false;

    // Raio ausente ou em texto vira NaN, e o OpenLayers guarda o círculo como um ponto de raio zero:
    // o extent sai finito e o ajuste iria a um ponto, não à cerca.
    if (geometry instanceof Circle && !(geometry.getRadius() > 0)) return false;
    const geometryExtent = geometry.getExtent();
    if (!geometryExtent.every(Number.isFinite)) return false;

    const extent = new CreateEmpty();
    extend(extent, geometryExtent);

    const features = [];
    const fence = new Feature({geometry, name: 'geofenceFocus'});
    fence.setId(+geofence.id);
    fence.setStyle(new Style({
      stroke: new Stroke({color: GEOFENCE_FOCUS_COLOR, width: 5}),
      fill: new Fill({color: 'rgba(255, 106, 0, 0.25)'}),
      zIndex: 1,
    }));
    features.push(fence);

    if (eventLonLat) {
      const coord = fromLonLat(eventLonLat);
      const pin = new Feature({geometry: new Point(coord), name: 'eventPosition'});
      pin.setStyle(new Style({
        image: new Icon({src: getSelectionPin(GEOFENCE_FOCUS_COLOR), anchor: [0.5, 1], scale: 0.55}),
        zIndex: 2,
      }));
      features.push(pin);
      extend(extent, [coord[0], coord[1], coord[0], coord[1]]);
    }
    if (!extent.every(Number.isFinite)) return false;

    this.clearGeofenceFocus();
    if (!this._geofenceFocusLayer) {
      this._geofenceFocusLayer = new VectorLayer({source: new VectorSource(), name: 'geofenceFocusLayer'});
      this._instance.addLayer(this._geofenceFocusLayer);
    }
    this._geofenceFocusLayer.getSource().addFeatures(features);

    const view = this._instance.getView();
    view.cancelAnimations();
    view.fit(extent, {
      size: this._instance.getSize(),
      padding: GEOFENCE_FOCUS_PADDING,
      maxZoom: GEOFENCE_FOCUS_MAX_ZOOM,
      duration: 500,
    });
    return true;
  }

  clearGeofenceFocus() {
    if (!this._geofenceFocusLayer) return;
    this._geofenceFocusLayer.getSource().clear();
  }

  wktToGeometry(area) {
    const mapView = this._instance.getView();
    let geometry;
    let projection;
    let resolutionAtEquator;
    let pointResolution;
    let resolutionFactor;
    const points = [];
    let center;
    let radius;
    let content;
    let i;
    let lat;
    let lon;
    let coordinates;

    if (area.lastIndexOf('POLYGON', 0) === 0) {
      content = area.match(/\([^()]+\)/);
      if (content !== null) {
        coordinates = content[0].match(/-?\d+\.?\d*/g);
        if (coordinates !== null) {
          projection = mapView.getProjection();
          for (i = 0; i < coordinates.length; i += 2) {
            lat = Number(coordinates[i]);
            lon = Number(coordinates[i + 1]);
            points.push(transform([lon, lat], 'EPSG:4326', projection));
          }
          geometry = new Polygon([points]);
        }
      }
    } else if (area.lastIndexOf('CIRCLE', 0) === 0) {
      content = area.match(/\([^()]+\)/);
      if (content !== null) {
        coordinates = content[0].match(/-?\d+\.?\d*/g);
        if (coordinates !== null) {
          projection = mapView.getProjection();
          center = transform([Number(coordinates[1]), Number(coordinates[0])], 'EPSG:4326', projection);
          resolutionAtEquator = mapView.getResolution();
          pointResolution = getPointResolution(projection, resolutionAtEquator, center);
          resolutionFactor = resolutionAtEquator / pointResolution;
          radius = Number(coordinates[2]) / METERS_PER_UNIT.m * resolutionFactor;
          geometry = new Circle(center, radius);
        }
      }
    } else if (area.lastIndexOf('LINESTRING', 0) === 0) {
      content = area.match(/\([^()]+\)/);
      if (content !== null) {
        coordinates = content[0].match(/-?\d+\.?\d*/g);
        if (coordinates !== null) {
          projection = mapView.getProjection();
          for (i = 0; i < coordinates.length; i += 2) {
            lat = Number(coordinates[i]);
            lon = Number(coordinates[i + 1]);
            points.push(transform([lon, lat], 'EPSG:4326', projection));
          }
          geometry = new LineString(points);
        }
      }
    }
    return geometry;
  }

  removeLayer(layer) {
    this._instance.removeLayer(layer);
    layer = null;
  }

  removeReports() {
    const source = this._reportLayer.getSource();
    source.getFeatures().forEach((f) => source.removeFeature(f));
    // O pino de seleção também saiu da camada: guardar a referência faria o próximo
    // removeReportSelectionPin pedir ao OpenLayers para remover uma feição que ele não tem mais.
    this._reportSelectionPin = null;
    this.selectedReportPoint = null;
    this.selectedReportDevice = null;
    this.removeReplayMarker();
  }

  removeGeofences() {
    if (!this._geofenceLayer) return;
    const source = this._geofenceLayer.getSource();
    source.getFeatures().forEach((f) => source.removeFeature(f));
  }

  removeDevices() {
    if (!this._devicesRawSource) return;
    const source = this._devicesRawSource;
    source.getFeatures().forEach((f) => source.removeFeature(f));
    this._devices.clear();
  }


  removeDeviceFeature(deviceId) {
    const feature = this._devices.get(deviceId);
    if (!feature) return;
    const source = this._devicesRawSource;
    // Na nossa versao pinada (ol 6.5.0) removeFeature joga TypeError se a feature nao
    // esta registrada nessa source (ja removida, ou _devices desalinhado da source) --
    // bug conhecido do proprio OL, corrigido depois no v6.10.0 (PR #12917, "Make
    // removeFeature consistent with other remove methods": vira no-op silencioso).
    // hasFeature (API publica, ol/source/Vector.js) replica aqui o mesmo guard.
    if (source.hasFeature(feature)) {
      source.removeFeature(feature);
    }
    this._devices.delete(deviceId);
  }

  markPendingReappearance(deviceId) {
    this._pendingReappearanceIds.add(deviceId);
  }

  consumePendingReappearance(deviceId) {
    return this._pendingReappearanceIds.delete(deviceId);
  }

  // Only tracks devices this map actually tried to plot (had a
  // feature, or hit the disabled guard in addFeatureDevice) - a fleet-wide
  // disabled/re-enabled cadastro event for a device this page never wanted
  // (e.g. single-device event pages) must never make it reappear here.
  handleDeviceDisabled(deviceId) {
    const hadFeature = !!this.getDeviceFeatureByDeviceId(deviceId);
    this.removeDeviceFeature(deviceId);
    if (hadFeature) this._trackedDisabledIds.add(deviceId);
  }

  handleDeviceEnabled(deviceId) {
    if (this._trackedDisabledIds.delete(deviceId) && !this.getDeviceFeatureByDeviceId(deviceId)) {
      this.markPendingReappearance(deviceId);
    }
  }

  removeOverlayById(overlayId) {
    const found = this._instance.getOverlayById(overlayId);

    if (found) {
      this._instance.removeOverlay(found);
    }
  }

  removeOverlays(typeName) {
    this._instance
      .getOverlays()
      .getArray()
      .filter((o) => (!typeName ? true : o.get('name') === typeName))
      .forEach((o) => this._instance.removeOverlay(o));
  }

  setTraceLayerVisible(visible) {
    if (this._traceLayer) this._traceLayer.setVisible(visible);
  }

  removeFeaturesTrace(deviceId) {
    if (!this._traceLayer) return;

    const source = this._traceLayer.getSource();
    if (deviceId) {
      const found = source.getFeatures().find((f) => f.getId() === deviceId);
      found && source.removeFeature(found);
      this._traces.delete(deviceId);
      return;
    }
    const features = source.getFeatures();
    this._forgetTraces(features);
    features.forEach((f) => source.removeFeature(f));
  }

  // _traces tambem guarda as trilhas de evento, entao so sai o que pertencia a camada de trilhas.
  _forgetTraces(features) {
    const gone = new Set(features);
    for (const [key, feature] of this._traces) {
      if (gone.has(feature)) this._traces.delete(key);
    }
  }

  removeFeaturesEventTrace(deviceId) {
    const source = this._traceEventLayer.getSource();
    if (deviceId) {
      const found = source.getFeatures().find((f) => f.getId() === deviceId);
      found && source.removeFeature(found);
      return;
    }
    source.getFeatures().forEach((f) => source.removeFeature(f));
  }

  updateAlarmOverlay(overlayId, overlay, {name, coord}) {
    const _overlay = overlay ?? this._instance.getOverlayById(overlayId);
    if (!_overlay) return;

    name && this.updateAlarmOverlayName(name, null, overlay);
    coord && this.updateOverlayPosition(null, coord, overlay);
  }

  updateAlarmOverlayName(name, overlayId, overlay) {
    let element;

    if (overlay) {
      element = overlay.getElement();
    } else {
      element = this._instance.getOverlayById(overlayId).getElement();
    }
    if (!element) return;

    element.children.namedItem('alarm-name').innerText = this._i18n.formatMessage({
      id: `alarm${String(name)
        .charAt(0)
        .toUpperCase()}${String(name).slice(1)}`,
    });
  }

  updateOverlayPosition(OverlayId, coord, overlay) {
    if (overlay) {
      overlay.setPosition(fromLonLat(coord));
      return;
    }

    const _overlay = this.findOverlayById(OverlayId);
    if (_overlay) {
      _overlay.setPosition(fromLonLat(coord));
    }
  }

  updateTrace(deviceId, coords) {
    const feature = this._traces.get(deviceId);
    const courses = [];

    coords = coords.map(([lon, lat, course]) => {
      courses.push(course);
      return fromLonLat([lon, lat]);
    });

    feature.get('data').courses = courses;

    feature.getGeometry().setCoordinates(coords);
  }

  updateEventTrace(deviceId, coords) {
    const feature = this._traces.get(deviceId);
    const courses = [];

    coords = coords.map(([lon, lat, course]) => {
      courses.push(course);
      return fromLonLat([lon, lat]);
    });

    if (feature) {
      feature.get('data').courses = courses;
      feature.getGeometry().setCoordinates(coords);
    }
  }

  updateDevicePosition(device, animateToPosition = false) {
    const {longitude, latitude, course, valid} = device.currentPosition;
    const coord = [longitude, latitude];
    const feature = this._devices.get(device.id);
    if (!feature) return;
    feature.get('data').course = course;
    feature.get('data').valid = valid;
    feature.getGeometry().setCoordinates(fromLonLat(coord));

    if (animateToPosition) {
      const currentZoom = this._instance.getView().getZoom();
      this.isFollowAnimating = true;
      this.goTo(coord, currentZoom);
    }
  }

  updateDevice(device) {
    if (this._devices.has(+device.id)) {
      const {category, status, id} = device;
      const {attributes = {}} = device.currentPosition || {};
      const ignition = attributes.ignition || false;

      let img = this._cacheImg[`${category}_${status}_${ignition.toString()}`];

      if (!img) {
        img = new Image(50, 50);
        img.src = getDeviceIcon(
          category,
          'top',
          getDeviceIconColor({status, ignition}),
        );
        this._cacheImg[`${category}_${status}_${ignition.toString()}`] = img;
      }

      const f = this._devices.get(+id);
      f.get('data').img = img;
      f.getStyle()(f);
    }
  }

  findLayerById(id) {
    return this.instance
      .getLayers()
      .getArray()
      .find((l) => l.get('id') === id);
  }

  findLayerByProperties(prop, value) {
    return this.instance
      .getLayers()
      .getArray()
      .find((l) => l.get(prop) == value);
  }

  _getDistance(start, end) {
    return Math.abs(
      this._instance.getPixelFromCoordinate(start)[0] -
      this._instance.getPixelFromCoordinate(end)[0],
    );
  }

  getDeviceFeatureByDeviceId(deviceId) {
    return this._devices.get(deviceId) ?? null;
  }

  findOverlayById(overlayId) {
    return this._instance.getOverlayById(overlayId);
  }

  setLayerVisibilityByLayerName(layerName, visible) {
    this.instance.getLayers().forEach((layer) => {
      if (layer.get('name') === layerName) {
        layer.setVisible(visible);
      }
    });
  }
}

export default MapInstance;
